import Vue from 'vue'
import buildURL from 'axios/lib/helpers/buildURL'
import BackendService from '@/services/Backend'

import { EVENT_TOKEN_UPDATE } from '@/config/events'
import { isRequired } from '@/utils/CustomErrors'

class OvpService extends BackendService {
  missingBasket = 'basketId missing!'
  missingToken = 'jwToken missing!'

  constructor(baseUrl, interceptor) {
    super(baseUrl, '/ovpv3', interceptor)

    this.$EventBus = Vue.prototype.$EventBus
  }

  /**
   * @param {Basket} payload Basket data
   * @returns {Promise}
   */
  createBasket(payload) {
    this.$EventBus.$emit(EVENT_TOKEN_UPDATE)
    return this.post('/baskets', payload).then(response => response.data)
  }

  createSignature(basketId, personId, { jwToken, signature }) {
    if (!jwToken) throw new Error(this.missingToken)
    if (!signature) throw new Error('signature missing!')

    const url = buildURL(`/baskets/${basketId}/signature/${personId}`, {
      jwToken,
    })
    return this.post(url, { signature })
  }

  /**
   *
   * @param {String} basketId  Session id from the basket
   * @param {String} personId  Unique id of the person
   * @param {String} payload.jwToken  Hash jwt token
   * @return {Promise}
   */
  deleteSignature(basketId, personId, payload) {
    const { jwToken } = payload
    const url = buildURL(`/baskets/${basketId}/signature/${personId}`, {
      jwToken,
    })
    return this.delete(url)
  }

  /**
   * @param {Basket} payload  Current basket
   *    @param {String} payload.basketId  Session id from the basket
   *    @param {String} payload.jwToken  Hash jwt token
   * @returns {Promise}
   */
  getBasket({ basketId, jwToken }) {
    if (!basketId) throw new Error(this.missingBasket)
    if (!jwToken) throw new Error(this.missingToken)

    this.$EventBus.$emit(EVENT_TOKEN_UPDATE)

    return this.get(`/baskets/${basketId}`, {
      jwToken,
      timestamp: Date.now(),
    }).then(response => response.data)
  }

  /**
   *
   * @param basketId
   * @param jwToken
   * @returns {Promise<Array<CancellationQuestion>>}
   */
  getCancellationQuestions({ basketId, jwToken }) {
    if (!basketId) throw new Error(this.missingBasket)
    if (!jwToken) throw new Error(this.missingToken)
    return this.get(`/baskets/${basketId}/cancellationQuestions`, {
      jwToken,
      basketId,
    }).then(response => response?.data?.cancellationQuestions)
  }

  getCollective({ contractNumber, contractStartDate }) {
    if (!contractNumber) throw new Error('contractNumber missing!')
    if (!contractStartDate) throw new Error('contractStartDate missing!')
    return this.get(`/collectiveconfig/${contractNumber}`, {
      contractStartDate,
    }).then(response => response.data)
  }

  getContract({ basketId, contractNumber, language, jwToken }) {
    if (!basketId) throw new Error(this.missingBasket)
    if (!jwToken) throw new Error(this.missingToken)
    if (!language) throw new Error('language missing!')
    if (!contractNumber) throw new Error('contractNumber missing!')
    return this.get(`/baskets/${basketId}/contracts/${contractNumber}`, {
      language,
      jwToken,
    }).then(response => response.data)
  }

  /**
   * Partner/collective show additional information and a logo on the person screen
   *
   * @param distributionPartnerNumber
   * @param contractStartDate
   * @param language
   * @returns {Promise<unknown>}
   */
  getDistributionPartnerInfo({ distributionPartnerNumber, contractStartDate, language }) {
    return this.get(`/distributionpartner/${distributionPartnerNumber}`, {
      language,
      contractStartDate,
    }).then(response => response.data)
  }

  getDistributionPartnerOrganizationName({ distributionPartnerNumber, contractStartDate, language }) {
    return this.get(`/distributionpartner/${distributionPartnerNumber}/organizationName`, {
      language,
      contractStartDate,
    }).then(response => response.data)
  }

  getDistributionPartnerContactInfo({ distributionPartnerNumber, language }) {
    return this.get(`/distributionpartner/${distributionPartnerNumber}/serviceCenter`, {
      language,
    }).then(response => response.data)
  }

  /**
   * @param {Basket} payload  Current basket
   *    @param {String} payload.basketId  Session id from the basket
   *    @param {String} payload.jwToken  Hash jwt token
   * @param {String} type  Document file type
   * @returns {Promise}
   */
  getDocumentUrl({ basketId, jwToken }, type) {
    if (!basketId) throw new Error(this.missingBasket)
    if (!jwToken) throw new Error(this.missingToken)
    if (!type) throw new Error('type missing!')

    return Promise.resolve(
      buildURL(`${this.baseUrl}/baskets/${basketId}/document.pdf`, {
        jwToken,
        type,
      })
    )
  }

  /**
   * interface IncentivePayload {
   *   basketId: UUID
   *   personId: UUID
   *   language: Language
   * }
   *
   * interface IncentiveResponse {
   *   data: Incentive
   * }
   *
   * @param {IncentivePayload} payload
   * @returns {Promise<IncentiveResponse>}
   */
  getIncentive(payload) {
    return this.get('/incentives', payload).then(response => response?.data)
  }

  /**
   * @param {Basket} payload  Current basket
   *    @param {String} payload.basketId  Session id from the basket
   *    @param {String} payload.jwToken  Hash jwt token
   * @returns {String}
   */
  getOverviewPdfUrl({ basketId, jwToken }) {
    if (!basketId) throw new Error(this.missingBasket)
    if (!jwToken) throw new Error(this.missingToken)

    return buildURL(`${this.baseUrl}/baskets/${basketId}/overview.pdf`, {
      jwToken,
    })
  }

  /**
   * @param {Basket} payload  Current basket
   *    @param {String} payload.contractStartDate  Contract start date from the basket
   *    @param {String}payload.masterTreaty  Master treaty from the basket
   *    @param {Object} [payload.collective]  Collective object from the basket
   * @returns {Promise}
   */
  getProducts({ masterTreaty, contractStartDate, collective }) {
    if (!contractStartDate) throw new Error('contractStartDate missing!')
    if (!masterTreaty) throw new Error('masterTreaty missing!')

    const payload = {
      channel: collective?.channel,
      collectiveContractNumber: collective?.contractNumber,
      contractStartDate: contractStartDate,
      masterTreaty: masterTreaty,
    }
    return this.get('products', payload).then(response => response?.data)
  }

  /**
   * @param payload  Current basket
   * @returns {Promise}
   */
  getRiskAssessment(payload) {
    return this.post('/risk-assessment', payload)
  }

  getSignature({ basketId, jwToken, personId }) {
    if (!basketId) throw new Error(this.missingBasket)
    if (!jwToken) throw new Error(this.missingToken)
    if (!personId) throw new Error('personId missing!')
    return this.get(`/baskets/${basketId}/signature/${personId}`, { jwToken })
  }

  /**
   * @param {String} payload.token  Reentry token from the basket
   * @param {Number} [payload.tan]  SMS tan which is four digits long
   * @returns {Promise}
   */
  loadBasket(payload) {
    this.$EventBus.$emit(EVENT_TOKEN_UPDATE)
    return this.get('/baskets', payload).then(response => response.data)
  }

  /**
   * @param {String} token  Reentry token from the basket
   * @returns {Promise}
   */
  sendSMSTan(token) {
    this.$EventBus.$emit(EVENT_TOKEN_UPDATE)
    return this.get('/baskets/smstan', { token }).then(response => response.data)
  }

  /**
   *
   * @param {String} basketId Session id from the basket
   * @param {String} payload.jwToken  Hash jwt token
   */
  signDocument(basketId, payload) {
    const { jwToken } = payload
    if (!basketId) throw new Error(this.missingBasket)
    if (!jwToken) throw new Error(this.missingToken)

    return this.get(`/baskets/${basketId}/signDocument`, { jwToken })
  }

  signupContest({ basketId, jwToken }) {
    if (!jwToken) throw new Error(this.missingToken)
    if (!basketId) throw new Error(this.missingBasket)

    const path = buildURL(`/baskets/${basketId}/contest`, { jwToken })
    return this.post(path)
  }

  /**
   * Simulates a basketUpdate (getting products, prices, etc.) but doesn't save to the database
   *
   * @param {Basket} payload  Current basket
   * @params {Object} params Extended put url parameters
   * @returns {Promise}
   */
  simulateUpdateBasket(payload, params = {}) {
    const path = buildURL(`/alva/updateProducts`, { ...params })
    return this.put(path, payload).then(response => response.data)
  }

  /**
   * @param {Basket} payload  Current basket
   *    @param {String} payload.basketId  Session id from the basket
   *    @param {String} payload.jwToken  Hash jwt token
   * @param {Object} params  Extended put url parameters
   * @returns {Promise}
   */
  updateBasket(payload, params = {}) {
    const { basketId, jwToken } = payload
    if (!basketId) throw new Error(this.missingBasket)
    if (!jwToken) throw new Error(this.missingToken)

    this.$EventBus.$emit(EVENT_TOKEN_UPDATE)

    const path = buildURL(`/baskets/${basketId}`, { jwToken, ...params })
    return this.put(path, payload).then(response => response.data)
  }

  createOrUpdateAdvisoryProtocol(payload, { jwToken = isRequired('jwToken') }) {
    const { basketId = isRequired('basketId'), submit = isRequired('submit') } = payload // query/url params must match payload

    const path = buildURL(`/baskets/${basketId}/advisory-protocol`, { jwToken, submit })
    return this.post(path, { ...payload }).then(response => response?.data)
  }

  deleteAdvisoryProtocol({ basketId = isRequired('basketId'), jwToken = isRequired('jwToken') }) {
    const path = buildURL(`/baskets/${basketId}/advisory-protocol`, { jwToken })
    return this.delete(path).then(response => response?.data)
  }
  getAdvisoryProtocol({ basketId = isRequired('basketId'), jwToken = isRequired('jwToken') }) {
    return this.get(`/baskets/${basketId}/advisory-protocol`, { jwToken }).then(response => response?.data)
  }

  getAdvisoryProtocolDocuments({ basketId = isRequired('basketId'), jwToken = isRequired('jwToken') }) {
    return this.get(`/baskets/${basketId}/advisory-protocol/documents`, { jwToken }).then(response => response?.data)
  }
}

export default OvpService
